import React, { useEffect } from 'react';
import type { History } from 'history';

const NavigationBlockerContext = React.createContext<History | null>(null);

interface NavigationBlockerProviderProps {
  history: History;
  children: React.ReactNode;
}

export function NavigationBlockerProvider({
  history,
  children,
}: NavigationBlockerProviderProps) {
  return (
    <NavigationBlockerContext.Provider value={history}>
      {children}
    </NavigationBlockerContext.Provider>
  );
}

export function useUnsavedChangesGuard(
  enabled: boolean,
  message: string
): void {
  const history = React.useContext(NavigationBlockerContext);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;

      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enabled, message]);

  useEffect(() => {
    if (!enabled || !history) {
      return;
    }

    const unblock = history.block((transition) => {
      if (!window.confirm(message)) {
        return;
      }

      unblock();
      transition.retry();
    });

    return unblock;
  }, [enabled, history, message]);
}
